<script lang="ts" setup generic="T extends string | number | symbol">
import { computed, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import AppSvgIcon from '../../appSvgIcon/AppSvgIcon.vue';
import SelectDropdown from '../common/SelectDropdown.vue';
import type {
  AppMultiSelectOption,
  AppMultiSelectSelectedPlaceholder,
  AppMultiSelectSize,
  AppMultiSelectType,
} from '../models/multi.model';
import { AppFadeInOutTransition } from '../../transitions';

import AppMultiSelectItem from './AppMultiSelectItem.vue';

interface AppMultiSelectPlaceholders {
  placeholder: string;
  selectedPlaceholder: string;
}

export interface AppMultiSelectProps<T> {
  type: AppMultiSelectType;
  options: AppMultiSelectOption<T>[];
  size?: AppMultiSelectSize;
  disabled?: boolean;
  placeholder?: string;
  selectedPlaceholder?: string;
  selectedPlaceholderFormat?: AppMultiSelectSelectedPlaceholder;
  shouldShowClearButton?: boolean;
  useTeleport?: boolean;
  wide?: boolean;
  isWrapperScrolling?: boolean;
}

// TODO: size of dropdown
// TODO: open up or down
const props = withDefaults(defineProps<AppMultiSelectProps<T>>(), {
  selectedPlaceholderFormat: 'x-selected',
  useTeleport: true,
});

interface AppMultiSelectEmits {
  (event: 'clear-selection'): void;
  (event: 'select', value: AppMultiSelectOption<T>[]): void;
  (event: 'select-single', value: AppMultiSelectOption<T>): void;
}

const emit = defineEmits<AppMultiSelectEmits>();

const isDropdownOpened = ref<boolean>(false);
const appMultiSelectRef = ref<HTMLElement | null>(null);
const appMultiSelectLabelRef = ref<HTMLElement | null>(null);

const xSelectedLabel = computed<AppMultiSelectPlaceholders>(() => ({
  placeholder: props.placeholder ?? 'Select',
  selectedPlaceholder: props.selectedPlaceholder ?? 'selected',
}));

const selectedOptions = computed<undefined | AppMultiSelectOption<T>[]>(() =>
  props.options.filter((option) => option.selected)
);

const haveSelectedOptions = computed<boolean>(
  () => selectedOptions.value !== undefined && selectedOptions.value.length > 0
);

const formatedXSelectedPlaceholder = computed<string>(
  () =>
    `${selectedOptions.value?.length} ${xSelectedLabel.value.selectedPlaceholder}`
);

const formatedSelectedPlaceholder = computed<string | undefined>(() =>
  props.selectedPlaceholderFormat === 'x-selected'
    ? formatedXSelectedPlaceholder.value
    : getFromatedFilterNameLabel()
);

const shouldShowClearButton = computed<boolean>(
  () =>
    props.shouldShowClearButton &&
    !!selectedOptions.value &&
    selectedOptions.value.length > 0
);

function getFromatedFilterNameLabel(): string | undefined {
  let label;
  if (!selectedOptions.value) {
    return;
  }
  if (selectedOptions.value.length === 0) {
    label = undefined;
  } else if (selectedOptions.value.length === 1 && selectedOptions.value[0]) {
    label = selectedOptions.value[0]?.label;
  } else if (selectedOptions.value[0]) {
    label = `${selectedOptions.value[0].label} + ${
      selectedOptions.value?.length - 1
    }`;
  }
  return label;
}

function toggleDropdown(): void {
  if (props.disabled) {
    return;
  }

  isDropdownOpened.value = !isDropdownOpened.value;
}

function onSelect(option: AppMultiSelectOption<T>): void {
  if (option.disabled) {
    return;
  }

  if (option.selected === undefined) {
    option.selected = true;
  } else {
    option.selected = !option.selected;
  }

  emit('select-single', option);
  emit('select', props.options);

  if (props.options.length === 1) {
    toggleDropdown();
  }
}

function onClearSelection(): void {
  if (isDropdownOpened.value) {
    toggleDropdown();
  }
  emit('clear-selection');
}

onClickOutside(appMultiSelectRef, (event) => {
  const dropdown = document.querySelector('.app-select-dropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }
  if (isDropdownOpened.value) {
    isDropdownOpened.value = false;
  }
});

watch(
  () => props.isWrapperScrolling,
  () => {
    if (props.isWrapperScrolling) {
      isDropdownOpened.value = false;
    }
  }
);
</script>

<template>
  <div
    ref="appMultiSelectRef"
    class="app-multi-select"
    :class="[
      props.type,
      props.size,
      {
        wide: props.wide,
        opened: isDropdownOpened,
        selected: selectedOptions && selectedOptions.length > 0,
      },
    ]"
  >
    <button
      ref="appMultiSelectLabelRef"
      class="app-multi-select-label"
      @click="toggleDropdown()"
    >
      <span class="app-multi-select-label-text">
        {{ haveSelectedOptions ? formatedSelectedPlaceholder : placeholder }}
      </span>
      <AppSvgIcon
        v-if="isDropdownOpened && !shouldShowClearButton"
        name="chevronUp"
        size="18"
      />
      <AppSvgIcon
        v-if="!isDropdownOpened && !shouldShowClearButton"
        name="chevronDown"
        size="18"
      />
      <AppSvgIcon
        v-if="shouldShowClearButton"
        name="closeInCircle"
        size="18"
        @click.stop="onClearSelection"
      />
    </button>
    <template v-if="props.useTeleport">
      <Teleport v-if="isDropdownOpened" to="body">
        <AppFadeInOutTransition>
          <SelectDropdown
            v-if="isDropdownOpened"
            :parent-ref="appMultiSelectLabelRef"
            :is-using-teleport="true"
          >
            <template #options>
              <AppMultiSelectItem
                v-for="item of props.options"
                :key="item.id"
                :item="item"
                @click="onSelect(item)"
              />
            </template>
          </SelectDropdown>
        </AppFadeInOutTransition>
      </Teleport>
    </template>
    <template v-else>
      <SelectDropdown
        v-if="isDropdownOpened"
        :parent-ref="appMultiSelectLabelRef"
        :is-using-teleport="false"
      >
        <template #options>
          <AppMultiSelectItem
            v-for="item of props.options"
            :key="item.id"
            :item="item"
            @click="onSelect(item)"
          />
        </template>
      </SelectDropdown>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../styles/_media';
@import '../../../styles/_fonts.scss';

.app-multi-select {
  position: relative;
  display: flex;

  white-space: nowrap;

  align-items: center;

  * {
    box-sizing: border-box;
  }

  &.size-l {
    .app-multi-select-label {
      padding: var(--spacing-12) var(--spacing-16);
      @include font-label-m-bold;
    }
    .app-multi-select-dropdown {
      top: 44px;
    }
  }

  &.size-s {
    .app-multi-select-label {
      padding: var(--spacing-4) var(--spacing-8);
      @include font-label-s-light;
    }
    .app-multi-select-dropdown {
      top: 26px;
    }
  }

  &.primary {
    .app-multi-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-neutral-70);
    }
  }

  &.modern {
    .app-multi-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-scrollbar);
      gap: var(--spacing-4);
      @include on-hover {
        background-color: var(--color-neutral-10);
        color: var(--color-neutral-70);
      }
    }
    &.opened {
      .app-multi-select-label {
        background-color: var(--color-white);
        border: 1px solid var(--color-soft-black);
      }
    }
    &.selected {
      .app-multi-select-label {
        background-color: var(--color-neutral-70);
        color: var(--color-white);
      }
    }
  }

  &.modern-light {
    .app-multi-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-neutral-70);
      gap: var(--spacing-4);

      @include on-hover {
        background-color: var(--color-neutral-10);
        color: var(--color-neutral-70);
      }
    }
    &.opened {
      .app-multi-select-label {
        background-color: var(--color-white);
      }
    }
    &.selected {
      .app-multi-select-label {
        background-color: var(--color-white);
        color: var(--color-neutral-70);
      }
    }
  }

  &.light {
    .app-multi-select-label {
      @include font-label-s-bold;
      background-color: var(--color-white);
      color: var(--color-neutral-70);
      padding: var(--spacing-8) var(--spacing-12);
      border-radius: var(--border-radius-8);
    }
  }

  &.wide {
    width: 100%;
    .app-multi-select-label {
      width: 100%;
    }
  }

  .app-multi-select-label {
    all: unset;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    padding: var(--spacing-8) var(--spacing-12);
    @include font-label-s-bold;
  }
}
</style>
